import { useCallback, useEffect, useState } from 'react';
import { Card, Modal, StatusBadge, FilterBar } from '@/components/ui';
import { Icon } from '@/components/ui/Icon';
import { superApi, type CatalogModel, type EngineRow } from '@/shared/api/superApi';
import { toast } from '@/components/ui/Toast';

/**
 * "מודלי AI העובדים במערכת" — לכל קטגוריה: המודלים המובנים (Gemini ישיר),
 * מנועי OpenRouter שאושרו, וכפתור הוספה מהקטלוג המלא של OpenRouter
 * (ציבורי, ~345 מודלים). תמלול מחייב בדיקת איכות עברית לפני אישור.
 */

const CATEGORIES: { kind: string; he: string; hint: string; needsBench: boolean }[] = [
  { kind: 'call_transcription', he: 'תמלול שיחות', hint: 'המודל שמתמלל כל שיחה מוקלטת (בורר "מודל תמלול" בכרטיס הלקוח).', needsBench: true },
  { kind: 'summarization', he: 'סיכום וניתוח שיחות', hint: 'המודל שמפיק סיכום, סנטימנט, ציון ומדדים (בורר "מודל סיכום").', needsBench: false },
  { kind: 'transcription', he: 'זיהוי מילות מפתח', hint: 'תמלול מקטעים תוך-שיחה להתראות מילות מפתח.', needsBench: true },
  { kind: 'emotion', he: 'ניתוח רגשות', hint: 'מנוע תעודת הזהות הרגשית לשיחות.', needsBench: false },
];

const BUILTIN_NOTE = 'מודלי Gemini הישירים (כלולים במפתח המערכתי) זמינים תמיד ואינם ניתנים להשבתה.';

export function AiModelsTab() {
  const [byKind, setByKind] = useState<Record<string, EngineRow[]>>({});
  const [browse, setBrowse] = useState<string | null>(null); // kind שנפתח לו הקטלוג
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async () => {
    const entries = await Promise.all(CATEGORIES.map(async (c) => {
      try {
        const d = await superApi.engines(c.kind);
        return [c.kind, d.engines] as const;
      } catch {
        return [c.kind, []] as const;
      }
    }));
    setByKind(Object.fromEntries(entries));
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function bench(e: EngineRow) {
    setBusyId(e.id);
    try {
      const next = await superApi.benchEngine(e.id);
      toast(next.bench?.passed ? 'בדיקת האיכות עברה ✓' : 'בדיקת האיכות נכשלה — ראה תוצאות', next.bench?.passed ? 'ok' : 'warn');
      void load();
    } catch {
      toast('שגיאה בהרצת הבדיקה', 'danger');
    } finally {
      setBusyId(null);
    }
  }

  async function setStatus(e: EngineRow, status: 'approved' | 'disabled', force = false) {
    setBusyId(e.id);
    try {
      await superApi.updateEngine(e.id, { status, force });
      toast(status === 'approved' ? 'המודל אושר ופעיל במערכת' : 'המודל הושבת', 'ok');
      void load();
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } }).response?.data?.message;
      toast(msg ?? 'שגיאה בעדכון', 'danger');
    } finally {
      setBusyId(null);
    }
  }

  async function remove(e: EngineRow) {
    try {
      await superApi.deleteEngine(e.id);
      void load();
    } catch {
      toast('שגיאה במחיקה', 'danger');
    }
  }

  return (
    <div className="stack" style={{ padding: 'var(--gap)' }}>
      <p style={{ margin: 0, color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>
        {BUILTIN_NOTE} מודל חיצוני (OpenRouter) שולח את התוכן לספק שלישי — שקול פרטיות לפני אישור.
      </p>
      {CATEGORIES.map((c) => (
        <Card key={c.kind} title={c.he} action={
          <button type="button" className="btn primary" onClick={() => setBrowse(c.kind)}>
            <Icon name="plus" size={14} /> הוסף מהקטלוג
          </button>
        }>
          <div className="stack">
            <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>{c.hint}</div>
            {(byKind[c.kind] ?? []).length === 0 ? (
              <div style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>אין מודלים חיצוניים — רק מודלי Gemini המובנים.</div>
            ) : (
              (byKind[c.kind] ?? []).map((e) => (
                <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '8px 10px', background: 'var(--surface-2)', borderRadius: 'var(--r-2)' }}>
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <code dir="ltr" style={{ fontSize: 'var(--t-sm)' }}>{e.model_id}</code>
                    {e.label && <span style={{ marginInlineStart: 8, color: 'var(--muted)', fontSize: 'var(--t-xs)' }}>{e.label}</span>}
                  </div>
                  <StatusBadge size="sm" tone={e.status === 'approved' ? 'ok' : e.status === 'disabled' ? 'danger' : 'warn'}>
                    {e.status === 'approved' ? 'פעיל במערכת' : e.status === 'disabled' ? 'מושבת' : 'מועמד'}
                  </StatusBadge>
                  {e.bench && (
                    <span style={{ fontSize: 'var(--t-xs)', color: e.bench.passed ? 'var(--ok)' : 'var(--danger)' }} dir="ltr">
                      bench: {e.bench.avg_overlap_pct}%
                    </span>
                  )}
                  {c.needsBench && e.status !== 'approved' && (
                    <button type="button" className="btn" disabled={busyId === e.id} onClick={() => bench(e)}>
                      {busyId === e.id ? 'בודק…' : 'בדוק איכות'}
                    </button>
                  )}
                  {e.status !== 'approved' ? (
                    <button type="button" className="btn primary" disabled={busyId === e.id}
                      // קטגוריות בלי bench (סיכום/רגשות) מאושרות תמיד ב-force
                      // (השרת דורש bench לכל אישור רגיל); בקטגוריות תמלול —
                      // force רק כשאין bench עובר, והכפתור אומר זאת במפורש.
                      onClick={() => setStatus(e, 'approved', !c.needsBench || !e.bench?.passed)}>
                      {c.needsBench && !e.bench?.passed ? 'אשר בכוח (ללא בדיקה)' : 'אשר'}
                    </button>
                  ) : (
                    <button type="button" className="btn" disabled={busyId === e.id} onClick={() => setStatus(e, 'disabled')}>השבת</button>
                  )}
                  <button type="button" className="btn ghost" aria-label="מחק" onClick={() => remove(e)}><Icon name="trash" size={13} /></button>
                </div>
              ))
            )}
          </div>
        </Card>
      ))}

      {browse && (
        <CatalogBrowser
          kind={browse}
          he={CATEGORIES.find((c) => c.kind === browse)?.he ?? ''}
          existing={(byKind[browse] ?? []).map((e) => e.model_id)}
          onClose={() => setBrowse(null)}
          onAdded={() => { setBrowse(null); void load(); }}
        />
      )}
    </div>
  );
}

function CatalogBrowser({ kind, he, existing, onClose, onAdded }: {
  kind: string; he: string; existing: string[]; onClose: () => void; onAdded: () => void;
}) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<CatalogModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    superApi.enginesCatalog(kind, q)
      .then((r) => { if (active) setRows(r); })
      .catch(() => { if (active) toast('שגיאה בטעינת הקטלוג', 'danger'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [kind, q]);

  async function add(m: CatalogModel) {
    setAdding(m.id);
    try {
      await superApi.addEngine(m.id, m.name, kind);
      toast('נוסף כמועמד — הרץ בדיקת איכות ואשר', 'ok');
      onAdded();
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } }).response?.data?.message;
      toast(msg ?? 'שגיאה בהוספה', 'danger');
    } finally {
      setAdding(null);
    }
  }

  return (
    <Modal isOpen onClose={onClose} title={`קטלוג OpenRouter — ${he}`} size="lg">
      <div className="stack">
        <FilterBar search={q} onSearch={setQ} onClear={() => setQ('')} placeholder="חיפוש מודל…" />
        {loading ? (
          <div style={{ color: 'var(--muted)' }}>טוען קטלוג…</div>
        ) : (
          <div style={{ maxHeight: 420, overflowY: 'auto' }} className="stack">
            {rows.slice(0, 60).map((m) => {
              const exists = existing.includes(m.id);
              return (
                <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', border: '1px solid var(--line)', borderRadius: 'var(--r-2)' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <code dir="ltr" style={{ fontSize: 'var(--t-sm)', fontWeight: 600 }}>{m.id}</code>
                      {m.audio_input && <StatusBadge size="sm" tone="accent">אודיו</StatusBadge>}
                      {m.transcribable && <StatusBadge size="sm" tone="ok">תמלול</StatusBadge>}
                    </div>
                    <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.description}</div>
                    <div dir="ltr" style={{ fontSize: 'var(--t-xs)', color: 'var(--text-2)', textAlign: 'end' }}>
                      ${m.price_in_per_m}/M in · ${m.price_out_per_m}/M out · ctx {Math.round(m.context_length / 1000)}K
                    </div>
                  </div>
                  <button type="button" className="btn primary" disabled={exists || adding === m.id} onClick={() => add(m)}>
                    {exists ? 'קיים' : adding === m.id ? 'מוסיף…' : 'הוסף'}
                  </button>
                </div>
              );
            })}
            {rows.length === 0 && <div style={{ color: 'var(--muted)' }}>לא נמצאו מודלים.</div>}
            {rows.length > 60 && <div style={{ color: 'var(--muted)', fontSize: 'var(--t-xs)' }}>מוצגים 60 הראשונים — חדד את החיפוש.</div>}
          </div>
        )}
      </div>
    </Modal>
  );
}
